/**
 * Shared stationary bookable-services selection policy.
 * Used by customer and partner so open-order / locked-service rules stay in sync.
 *
 * Callers should normalize missing `isCustomerEditable` to `true` (catalog default)
 * before calling, or rely on `!== false` / `=== false` semantics below.
 */

import type { MoneyRecord, OpenOrderRecord } from '@navio/nsdk';

type BookableServicePolicyRow = {
  salesItemId: number;
  /** When false, the service is locked (open order / non-editable catalog). Missing → editable. */
  isCustomerEditable?: boolean | null;
};

type StationaryServicePolicy<T extends BookableServicePolicyRow> = {
  /** Required / locked rows (explicit non-editable flags, else lockedSalesItemId fallback). */
  nonEditable: T[];
  /** Catalog rows the user may pick (excludes nonEditable). */
  selectable: T[];
  /** Row matching lockedSalesItemId, if present. */
  locked: T | null;
  /**
   * Single-primary enforcement (customer single-car flow):
   * locked-only catalog → first nonEditable; else the lockedSalesItemId row.
   * Partner multi-line flows should auto-select all `nonEditable` instead.
   */
  enforcedPrimary: T | null;
};

/** How locked/open-order services are auto-selected into the booking draft. */
type StationaryServiceEnforceMode = 'single-primary' | 'all-non-editable';

export function resolveStationaryServicePolicy<T extends BookableServicePolicyRow>(
  services: T[],
  lockedSalesItemId: number | null
): StationaryServicePolicy<T> {
  const fixed = services.filter((s) => s.isCustomerEditable === false);
  const locked = lockedSalesItemId != null ? (services.find((s) => s.salesItemId === lockedSalesItemId) ?? null) : null;
  // Prefer explicit non-editable flags; fall back to lockedSalesItemId for older APIs.
  const nonEditable = fixed.length > 0 ? fixed : locked != null ? [locked] : [];
  const nonEditableIds = new Set(nonEditable.map((s) => s.salesItemId));
  // Selectable excludes anything already treated as required/locked (no duplicate rows).
  const selectable = services.filter((s) => s.isCustomerEditable !== false && !nonEditableIds.has(s.salesItemId));
  const enforcedPrimary = nonEditable.length > 0 && selectable.length === 0 ? (nonEditable[0] ?? null) : locked;

  return { nonEditable, selectable, locked, enforcedPrimary };
}

/**
 * Services that must be auto-selected for a given enforce mode.
 * - single-primary: customer single-car (enforcedPrimary only)
 * - all-non-editable: partner multi-line (every locked/open-order primary)
 */
export function stationaryServicesToAutoSelect<T extends BookableServicePolicyRow>(
  policy: StationaryServicePolicy<T>,
  mode: StationaryServiceEnforceMode
): T[] {
  if (mode === 'all-non-editable') {
    return policy.nonEditable;
  }
  return policy.enforcedPrimary ? [policy.enforcedPrimary] : [];
}

/** Auto-add extras and catalog-locked items are included, not optional. */
export const isLockedBookingSalesItem = (item: { isCustomerEditable?: boolean | null }) =>
  item.isCustomerEditable === false;

/** Non-editable service as the included-items summary needs it. */
type IncludedServiceRow = {
  salesItemId: number;
  name: string;
  grossPrice: MoneyRecord;
  priceListName?: string | null;
  discountPercent?: number | null;
};

/** One row of the included-items summary: a non-editable service or an open-order line. */
export type IncludedOrderLine = {
  /** Stable within one summary — open-order lines carry no id of their own. */
  key: string;
  name: string;
  /** Only open-order lines have one; a service is always a single unit. */
  quantity?: number;
  grossAmount: MoneyRecord;
  priceListName?: string | null;
  discountPercent?: number | null;
};

/**
 * Everything the customer gets on this booking without choosing it, as one list:
 * locked/non-editable services first, then the residual lines of the open order.
 */
export function resolveIncludedOrderLines({
  fixedServices = [],
  openOrder
}: {
  fixedServices?: IncludedServiceRow[];
  openOrder?: OpenOrderRecord | null;
}): IncludedOrderLine[] {
  return [
    ...fixedServices.map((service) => ({
      key: `service-${service.salesItemId}`,
      name: service.name,
      grossAmount: service.grossPrice,
      priceListName: service.priceListName,
      discountPercent: service.discountPercent
    })),
    ...(openOrder?.lines ?? []).map((line, lineIndex) => ({
      key: `order-${openOrder?.externalOrderId ?? 'open'}-${line.salesItemId ?? 'line'}-${lineIndex}`,
      name: line.name,
      quantity: line.quantity,
      grossAmount: line.grossAmount
    }))
  ];
}

/**
 * Summed gross of the included lines, or null when there are none.
 * Every line is priced in the department currency, so the first one carries it.
 */
export function sumIncludedOrderLines(lines: IncludedOrderLine[]): MoneyRecord | null {
  const firstLine = lines[0];
  if (!firstLine) {
    return null;
  }
  return {
    amount: lines.reduce((sum, line) => sum + line.grossAmount.amount, 0),
    currency: firstLine.grossAmount.currency
  };
}
